import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, DataTable, StatusPill, KPITile } from "../components/Common";
import { Send, Mail, Eye, Download } from "lucide-react";
import { toast } from "sonner";

export default function Distribution() {
  const [docs, setDocs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const loadEntries = () => api.distributionList().then(({ data }) => setEntries(data.entries || []));

  useEffect(() => {
    api.archive().then(({ data }) => setDocs(data.documents || []));
    loadEntries();
    const t = setInterval(loadEntries, 5000);
    return () => clearInterval(t);
  }, []);

  const send = async () => {
    if (!selected) return toast.error("Select a document");
    setBusy(true);
    try {
      const { data } = await api.distribute(selected, { subject, message, recipients: [] });
      toast.success(`Distributed to ${data.queued} recipient(s)`);
      loadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Distribution failed");
    }
    setBusy(false);
  };

  const delivered = entries.filter((e) => e.status === "delivered" || e.status === "read").length;
  const read = entries.filter((e) => e.status === "read").length;

  return (
    <div data-testid="distribution-page">
      <PageHeader
        kicker="Secure Document Distribution"
        title="Distribution Outbox"
        subtitle="Deliver archived Education360 documents to parents and executives with HMAC-signed secure links, delivery tracking and read receipts. Delivery layer is currently MOCKED — swap for Resend/SendGrid without changing routes."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPITile tone="dark" testId="dist-total" label="Total Sent" value={entries.length} />
        <KPITile testId="dist-delivered" label="Delivered" value={delivered} accent="mocked SMTP" />
        <KPITile testId="dist-read" label="Read" value={read} />
        <KPITile tone="dark" testId="dist-queue" label="Queued" value={entries.filter((e) => e.status === "queued").length} />
      </div>

      <Section title="Send a document" testId="dist-send">
        <div className="hl-card p-6 space-y-3 max-w-3xl">
          <div>
            <label className="overline block mb-1.5">Archived Document</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)}
              data-testid="dist-doc" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="">— select —</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{d.subject_label} · v{d.version} · {d.period}</option>)}
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Termly Academic Report"
                data-testid="dist-subject"
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
            </div>
            <div>
              <label className="overline block mb-1.5">Recipients</label>
              <div className="text-xs text-slate-500 mt-2">Auto-resolved from student's registered guardians when left blank.</div>
            </div>
          </div>
          <div>
            <label className="overline block mb-1.5">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              rows={3} data-testid="dist-message"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
              placeholder="Please find attached the latest official document from Highlands International College." />
          </div>
          <button onClick={send} disabled={busy}
            data-testid="dist-send-btn"
            className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-40">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Securely"}
          </button>
        </div>
      </Section>

      <Section title="Distribution history" testId="dist-history">
        <DataTable
          columns={[
            { key: "document_label", label: "Document" },
            { key: "recipient", label: "Recipient", render: (r) => (
              <div>
                <div className="text-slate-800">{r.recipient_name}</div>
                <div className="text-xs text-slate-500">{r.recipient_email}</div>
              </div>
            ) },
            { key: "channel", label: "Channel", render: (r) => <span className="capitalize">{r.channel}</span> },
            { key: "status", label: "Status", render: (r) => <StatusPill status={r.status === "delivered" ? "resolved" : r.status === "read" ? "info" : r.status === "queued" ? "warning" : "danger"} /> },
            { key: "delivered_at", label: "Delivered", render: (r) => (r.delivered_at || "—").slice(11, 19) },
            { key: "read_at", label: "Read", render: (r) => (r.read_at || "—").slice(11, 19) },
            { key: "expires_at", label: "Link Expires", render: (r) => (r.expires_at || "").slice(0, 10) },
          ]}
          rows={entries}
          empty="No documents distributed yet."
        />
      </Section>
    </div>
  );
}
